import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import animation from "../../assets/analysis-animation.gif";

const translations = {
  en: {
    // Progress messages
    preparingHeader: "Preparing documents for analysis...",
    bigFiveHeader: "Analyzing Big Five personality traits...",
    discHeader: "Processing DISC behavioral styles...",
    mbtiHeader: "Determining MBTI personality type...",
    riasecHeader: "Evaluating RIASEC career interests...",
    finalizingHeader: "Finalizing comprehensive personality insights...",

    // Stage points
    initialization: [
      "Extracting CV content...",
      "Processing motivation letter...",
      "Analyzing LinkedIn profile data...",
      "Integrating career preferences...",
      "Preparing data for analysis...",
      "Initializing personality assessment...",
      "Preparing document processing...",
      "Extracting relevant sections...",
      "Converting document formats...",
    ],
    bigFive: [
      "Analyzing openness to experience levels...",
      "Measuring conscientiousness patterns...",
      "Evaluating extraversion tendencies...",
      "Assessing agreeableness traits...",
      "Analyzing creative thinking indicators...",
      "Measuring organization and planning skills...",
      "Evaluating social engagement preferences...",
      "Assessing team cooperation style...",
      "Measuring stress management capacity...",
    ],
    disc: [
      "Measuring dominance (D) factor...",
      "Analyzing influence (I) behaviors...",
      "Evaluating steadiness (S) patterns...",
      "Calculating conscientiousness (C) score...",
      "Measuring assertiveness levels...",
      "Analyzing communication style preferences...",
      "Evaluating team relationship dynamics...",
      "Measuring leadership approach...",
      "Analyzing decision-making speed...",
      "Assessing conflict resolution style...",
    ],
    mbti: [
      "Analyzing extraversion vs. introversion...",
      "Measuring sensing vs. intuition preference...",
      "Evaluating thinking vs. feeling tendency...",
      "Calculating judging vs. perceiving score...",
      "Measuring information collection style...",
      "Evaluating decision-making approach...",
      "Analyzing external world interaction style...",
      "Measuring cognitive function stack...",
      "Determining MBTI personality type...",
    ],
    riasec: [
      "Measuring realistic (R) career interest...",
      "Analyzing investigative (I) tendencies...",
      "Evaluating artistic (A) inclinations...",
      "Calculating social (S) orientation score...",
      "Measuring enterprising (E) behaviors...",
      "Analyzing conventional (C) preferences...",
      "Evaluating primary career interest type...",
      "Determining secondary interest pattern...",
      "Analyzing job role compatibility...",
      "Generating career recommendations...",
    ],
    finalizing: [
      "Creating comprehensive personality profile...",
      "Calculating job fit scores...",
      "Determining optimal work environments...",
      "Generating management strategy recommendations...",
      "Creating onboarding approach suggestions...",
      "Measuring model confidence levels...",
      "Generating development recommendations...",
      "Creating interview strategy suggestions...",
      "Analyzing predictive validity scores...",
      "Finalizing comprehensive analysis report...",
    ],
  },
  nl: {
    // Progress messages
    preparingHeader: "Documenten voorbereiden voor analyse...",
    bigFiveHeader: "Big Five persoonlijkheidstrekken analyseren...",
    discHeader: "DISC gedragsstijlen verwerken...",
    mbtiHeader: "MBTI persoonlijkheidstype bepalen...",
    riasecHeader: "RIASEC carrière-interesses evalueren...",
    finalizingHeader: "Uitgebreide persoonlijkheidsinzichten afronden...",

    // Stage points
    initialization: [
      "CV-inhoud extraheren...",
      "Motivatiebrief verwerken...",
      "LinkedIn-profielgegevens analyseren...",
      "Carrièrevoorkeuren integreren...",
      "Gegevens voorbereiden voor analyse...",
      "Persoonlijkheidsbeoordeling initialiseren...",
      "Documentverwerking voorbereiden...",
      "Relevante secties extraheren...",
      "Documentformaten converteren...",
    ],
    bigFive: [
      "Openheid voor ervaring analyseren...",
      "Consciëntieusheidspatronen meten...",
      "Extraversietendensen evalueren...",
      "Meegeeflijkheidstrekken beoordelen...",
      "Creatief denkvermogen analyseren...",
      "Organisatie- en planningsvaardigheden meten...",
      "Sociale betrokkenheidsvoorkeuren evalueren...",
      "Teamcoöperatiestijl beoordelen...",
      "Stressmanagementcapaciteit meten...",
    ],
    disc: [
      "Dominantie (D) factor meten...",
      "Invloed (I) gedrag analyseren...",
      "Stabiliteit (S) patronen evalueren...",
      "Consciëntieusheid (C) score berekenen...",
      "Assertiviteitsniveaus meten...",
      "Communicatiestijlvoorkeuren analyseren...",
      "Teamrelatiedynamiek evalueren...",
      "Leiderschapsaanpak meten...",
      "Besluitvormingssnelheid analyseren...",
      "Conflictoplossingsstijl beoordelen...",
    ],
    mbti: [
      "Extraversie vs. introversie analyseren...",
      "Waarneming vs. intuïtie voorkeur meten...",
      "Denken vs. voelen tendens evalueren...",
      "Oordelen vs. waarnemen score berekenen...",
      "Informatieverzamelingsstijl meten...",
      "Besluitvormingsaanpak evalueren...",
      "Externe wereldinteractiestijl analyseren...",
      "Cognitieve functiestapel meten...",
      "MBTI persoonlijkheidstype bepalen...",
    ],
    riasec: [
      "Realistische (R) carrière-interesse meten...",
      "Onderzoekende (I) tendensen analyseren...",
      "Artistieke (A) neigingen evalueren...",
      "Sociale (S) oriëntatiescore berekenen...",
      "Ondernemende (E) gedragingen meten...",
      "Conventionele (C) voorkeuren analyseren...",
      "Primair carrière-interessetype evalueren...",
      "Secundair interessepatroon bepalen...",
      "Jobrolcompatibiliteit analyseren...",
      "Carrière-aanbevelingen genereren...",
    ],
    finalizing: [
      "Uitgebreid persoonlijkheidsprofiel maken...",
      "Job-fit scores berekenen...",
      "Optimale werkomgevingen bepalen...",
      "Managementstrategie-aanbevelingen genereren...",
      "Onboarding-aanpak suggesties maken...",
      "Modelbetrouwbaarheidsniveaus meten...",
      "Ontwikkelingsaanbevelingen genereren...",
      "Interviewstrategie suggesties maken...",
      "Voorspellende validiteitsscores analyseren...",
      "Uitgebreid analyserapport afronden...",
    ],
  },
};

const ProgressBar = ({ progress = 0 }) => {
  const [currentPoint, setCurrentPoint] = useState("");
  const [animationState, setAnimationState] = useState("fade-in");
  const [currentTranslations, setCurrentTranslations] = useState(
    translations.en
  );

  useEffect(() => {
    const cookieIsDutch = Cookies.get("isDutch") === "true";
    setCurrentTranslations(translations[cookieIsDutch ? "nl" : "en"]);
  }, []);

  useEffect(() => {
    const stagePoints = {
      initialization: currentTranslations.initialization,
      bigFive: currentTranslations.bigFive,
      disc: currentTranslations.disc,
      mbti: currentTranslations.mbti,
      riasec: currentTranslations.riasec,
      finalizing: currentTranslations.finalizing,
    };

    const getCurrentStagePoints = () => {
      if (progress < 10) return stagePoints.initialization;
      if (progress < 30) return stagePoints.bigFive;
      if (progress < 50) return stagePoints.disc;
      if (progress < 70) return stagePoints.mbti;
      if (progress < 85) return stagePoints.riasec;
      return stagePoints.finalizing;
    };

    const fadeOutTime = 700;
    const pointDisplayTime = 2500;

    const pointsInterval = setInterval(() => {
      const currentStagePoints = getCurrentStagePoints();
      setAnimationState("fade-out");

      setTimeout(() => {
        const newPoint =
          currentStagePoints[
            Math.floor(Math.random() * currentStagePoints.length)
          ];
        setCurrentPoint(newPoint);
        setAnimationState("fade-in");
      }, fadeOutTime);
    }, pointDisplayTime);

    // Set an initial point immediately
    const initialStagePoints = getCurrentStagePoints();
    setCurrentPoint(
      initialStagePoints[Math.floor(Math.random() * initialStagePoints.length)]
    );

    return () => clearInterval(pointsInterval);
  }, [progress, currentTranslations]);

  const getProgressMessage = () => {
    if (progress < 10) return currentTranslations.preparingHeader;
    if (progress < 30) return currentTranslations.bigFiveHeader;
    if (progress < 50) return currentTranslations.discHeader;
    if (progress < 70) return currentTranslations.mbtiHeader;
    if (progress < 85) return currentTranslations.riasecHeader;
    return currentTranslations.finalizingHeader;
  };

  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg w-4/5 max-w-3xl">
        <div className="flex justify-center mb-4">
          <img src={animation} alt="Loading..." className="w-auto h-32" />
        </div>
        <h2 className="text-2xl font-bold mb-4 text-center">
          {getProgressMessage() || "Loading..."}
        </h2>
        <div className="flex gap-2 items-center mb-6">
          <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="absolute h-full gradient-bg-5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="font-semibold min-w-[3rem] text-right">
            {progress}%
          </span>
        </div>
        <div className="mt-16 rounded-lg">
          <div className="flex items-center justify-center">
            <div
              className={`flex justify-center items-center text-xl font-medium text-gray-800 transition-opacity duration-700 ${
                animationState === "fade-in" ? "opacity-100" : "opacity-0"
              }`}
            >
              {currentPoint}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;
